/**
 * UnlistedSource
 *
 * The escape hatch from the source picker: a prospect whose stack we have no
 * connector for describes it, and the request reaches the team.
 *
 * The promise shown on submit is deliberately modest and matches what the
 * pricing page says — an estimate within one business day, a typical connector
 * in 2–3 weeks. It also points at manual upload, which works today: the honest
 * answer to "we can't connect your system yet" is the thing they can do now,
 * not only the thing we might build later.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { useToast } from '@/contexts/ToastContext';
import { logError } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { CONTROL_HEIGHT, CONTROL_MIN_HEIGHT } from '@/components/ui/control-size';

interface UnlistedSourceProps {
  /** Called after a successful submit; the caller moves the user to import. */
  onSubmitted: () => void;
  onBack: () => void;
}

const FIELD_BASE = cn(
  'mt-1 w-full rounded-lg border border-border bg-bg-surface px-3',
  'text-sm text-fg placeholder:text-fg-subtle',
  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
);
// A one-line input takes its height from the control scale; the textarea grows
// with its rows, so it keeps vertical padding and a floor instead.
const INPUT_CLASS = cn(CONTROL_HEIGHT.md, FIELD_BASE);
const TEXTAREA_CLASS = cn(FIELD_BASE, 'min-h-20 py-2');

export function UnlistedSource({ onSubmitted, onBack }: UnlistedSourceProps) {
  const { t } = useTranslation(['settings', 'common']);
  const { authenticatedFetch } = useAuthFetch();
  const { toast } = useToast();

  const [crm, setCrm] = useState('');
  const [telephony, setTelephony] = useState('');
  const [recordingsLocation, setRecordingsLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Something has to identify the stack, but which field carries it is theirs
  // to choose — a call-centre with no CRM is a perfectly normal answer.
  const canSubmit =
    !submitting && (crm.trim() || telephony.trim() || recordingsLocation.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      await authenticatedFetch('/api/v1/sources/unlisted-request', {
        method: 'POST',
        body: JSON.stringify({ crm, telephony, recordingsLocation, notes }),
      });

      toast.success(
        t('dataSource.wizard.unlisted.sentTitle', 'Request sent'),
        t(
          'dataSource.wizard.unlisted.sentBody',
          "We'll come back with an estimate within one business day. A new connector typically takes 2–3 weeks. Meanwhile you can upload recordings manually — that works today."
        )
      );
      onSubmitted();
    } catch (err) {
      logError(
        'Unlisted source request failed',
        undefined,
        err instanceof Error ? err : undefined
      );
      toast.error(
        t('dataSource.wizard.unlisted.failed', 'Could not send the request'),
        err instanceof Error ? err.message : t('common:errors.unknown')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="unlisted-source">
      <div>
        <h3 className="text-sm font-semibold text-fg">
          {t('dataSource.wizard.unlisted.title', 'Tell us about your system')}
        </h3>
        <p className="mt-1 text-xs text-fg-muted">
          {t(
            'dataSource.wizard.unlisted.subtitle',
            "We build connectors on request. Tell us what you use and we'll estimate it."
          )}
        </p>
      </div>

      <label className="block text-xs text-fg-muted">
        {t('dataSource.wizard.unlisted.crm', 'CRM')}
        <input
          className={INPUT_CLASS}
          value={crm}
          onChange={(e) => setCrm(e.target.value)}
          maxLength={200}
          placeholder={t('dataSource.wizard.unlisted.crmPlaceholder', 'e.g. HubSpot')}
        />
      </label>

      <label className="block text-xs text-fg-muted">
        {t('dataSource.wizard.unlisted.telephony', 'Telephony')}
        <input
          className={INPUT_CLASS}
          value={telephony}
          onChange={(e) => setTelephony(e.target.value)}
          maxLength={200}
          placeholder={t(
            'dataSource.wizard.unlisted.telephonyPlaceholder',
            'e.g. Twilio'
          )}
        />
      </label>

      <label className="block text-xs text-fg-muted">
        {t('dataSource.wizard.unlisted.recordings', 'Where recordings are stored')}
        <input
          className={INPUT_CLASS}
          value={recordingsLocation}
          onChange={(e) => setRecordingsLocation(e.target.value)}
          maxLength={200}
          placeholder={t(
            'dataSource.wizard.unlisted.recordingsPlaceholder',
            'e.g. in the CRM, S3, provider portal'
          )}
        />
      </label>

      <label className="block text-xs text-fg-muted">
        {t('dataSource.wizard.unlisted.notes', 'Anything else (optional)')}
        <textarea
          className={TEXTAREA_CLASS}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
          rows={3}
        />
      </label>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className={cn(
            CONTROL_MIN_HEIGHT.md,
            'inline-flex items-center gap-2 rounded-lg border border-border px-4',
            'text-sm text-fg transition-colors hover:bg-bg-elevated',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
          )}
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common:common.back', 'Back')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className={cn(
            CONTROL_MIN_HEIGHT.md,
            'inline-flex items-center gap-2 rounded-lg bg-primary px-4',
            'text-sm font-medium text-bg transition-colors hover:bg-primary-hover',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
            'disabled:opacity-50'
          )}
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('dataSource.wizard.unlisted.submit', 'Send request')}
        </button>
      </div>
    </form>
  );
}
